import LibGenerateTestUserSig from '@msb/trtcsign' // 请移除，客户端计算token并不安全
import TIM, { ChatSDK } from '@tencentcloud/chat'
import EventEmitter from 'events'

import { RtmOptions } from '.'
import { DeMessage, EncodeOrDecode, MessageType } from './encodeOrDecode'

const generator = new LibGenerateTestUserSig(Number(import.meta.env.VITE_APP_TRTC_APPID), import.meta.env.VITE_APP_TRTC_SECRET, 1 * 24 * 60 * 60)

// 错误代码常量
const ErrorCodes = {
  GROUP_ID_IN_USE: 10021, // 群ID 已存在
  GROUP_ID_IN_USE_BY_OWNER: 10025, // 群ID 已存在，并且是群主
  IS_MEMBER_ALREADY: 10013 // 用户已经是群成员
}

export default class Tim extends EventEmitter {
  private appId: number
  private client!: ChatSDK
  private channel: string = ''
  private token: string | undefined = undefined
  private msgVersion: number = 3
  private uid: number = 0

  constructor(appId: string) {
    super()
    this.appId = Number(appId)
  }

  public async init({ uid, channel, token, version }: RtmOptions) {
    // 清理旧的监听器
    this.removeAllListeners()

    this.uid = uid
    this.channel = '100' + channel
    this.token = token
    this.msgVersion = version || this.msgVersion
    this.client = TIM.create({ SDKAppID: this.appId })

    this.subscribeClientEvents()
  }

  private subscribeClientEvents(): void {
    console.log('ConnectionStateChanged')
    this.client.on(TIM.EVENT.NET_STATE_CHANGE, (event: any) => {
      // 网络状态发生改变
      // event.name - TencentCloudChat.EVENT.NET_STATE_CHANGE
      // event.data.state 当前网络状态，枚举值及说明如下：
      //   - TencentCloudChat.TYPES.NET_STATE_CONNECTED - 已接入网络
      //   - TencentCloudChat.TYPES.NET_STATE_CONNECTING - 连接中。很可能遇到网络抖动，SDK 在重试。接入侧可根据此状态提示“当前网络不稳定”或“连接中”
      //   - TencentCloudChat.TYPES.NET_STATE_DISCONNECTED - 未接入网络。接入侧可根据此状态提示“当前网络不可用”。SDK 仍会继续重试，若用户网络恢复，SDK 会自动同步消息

      console.log('ConnectionStateChanged', event.data.state)
      switch (event.data.state) {
        case TIM.TYPES.NET_STATE_CONNECTED:
          console.log('TIM tim-connected')
          super.emit('tim-connected')
          break
        case TIM.TYPES.NET_STATE_CONNECTING:
          super.emit('tim-conntcting')
          break
        case TIM.TYPES.NET_STATE_DISCONNECTED:
          super.emit('tim-reconnecting')
          break
        default:
          return ''
      }
    })

    this.client.on(TIM.EVENT.KICKED_OUT, (event: any) => {
      console.log(`TIM User kicked out due to: ${event.data.type}`)
      switch (event.data.type) {
        case TIM.TYPES.KICKED_OUT_MULT_ACCOUNT:
          super.emit('user-kicked', 'multiple-accounts')
          break
        case TIM.TYPES.KICKED_OUT_MULT_DEVICE:
          super.emit('user-kicked', 'multiple-devices')
          break
        case TIM.TYPES.KICKED_OUT_USERSIG_EXPIRED:
          super.emit('user-userSig-expired')
          break
      }
    })
  }

  public async join() {
    if (!this.client) {
      throw new Error('TIM Client is not initialized.')
    }
    this.subscribeChannelClientEvents()

    this.client.on(TIM.EVENT.SDK_READY, async (event: any) => {
      try {
        const groupProfile = await this.client.getGroupProfile({ groupID: this.channel }) // 这种群模式web端这个地方一定会报错,如果是10007就是存在群,否则就是不存在群
        console.log('TIM getGroupProfile', groupProfile)
        this.joinGroup()
      } catch (error: any) {
        if (error.code === 10007) {
          this.joinGroup()
        } else {
          console.log('TIM Failed to join group, attempting to create group:', error.code)
          this.handleGroupCreation()
        }
      }
    })

    await this.client
      .login({
        userID: `${this.uid}`,
        userSig: generator.genTestUserSig(`${this.uid}`)
      })
      .catch((error: any) => {
        super.emit('login-failure')
      })
  }

  private async joinGroup() {
    this.client
      .joinGroup({ groupID: this.channel })
      .then((response) => {
        console.log('joinGroup', response)
        const status = response.data.status
        switch (status) {
          case TIM.TYPES.JOIN_STATUS_WAIT_APPROVAL:
            // 处理等待审批的逻辑
            break
          case TIM.TYPES.JOIN_STATUS_SUCCESS:
          case TIM.TYPES.JOIN_STATUS_ALREADY_IN_GROUP:
            this.emitJoinRoom()
            break
          default:
            // 处理其他未知状态
            break
        }
      })
      .catch((error: any) => {
        console.log('TIM joinGroup Error', error.code)
        console.log('TIM joinGroup Error', error.message)
        if (error.code === ErrorCodes.IS_MEMBER_ALREADY) {
          this.emitJoinRoom() // 已是群成员直接 发送进房成功
        }
      })
  }

  private async handleGroupCreation(): Promise<void> {
    try {
      const response = await this.client.createGroup({
        type: TIM.TYPES.GRP_MEETING,
        name: this.channel,
        groupID: this.channel
      })
      console.log('TIM Group created successfully:', response)
      this.emitJoinRoom()
    } catch (error) {
      console.error('TIM Failed to create group:', error)
    }
  }

  private emitJoinRoom(): void {
    this.emit('join-room', this.uid)
  }

  private subscribeChannelClientEvents(): void {
    this.client.on(TIM.EVENT.MESSAGE_RECEIVED, (messageList: any) => {
      console.log('TIM messageList', messageList)
      messageList.data.filter((item: any) => {
        // 处理 群成员 加入 退出
        // console.log('TIM user-add', item, this.channel, item.payload.operationType)
        if (item.to === this.channel && item.type === TIM.TYPES.MSG_GRP_TIP) {
          switch (item.payload.operationType) {
            case TIM.TYPES.GRP_TIP_MBR_JOIN:
              if (item.payload.operatorID != this.uid) {
                // 自己加入群的话也会触发 所以这里过滤掉自己的userid 进群通知
                console.log('TIM user-add', item.payload.operatorID)
                super.emit('user-add', item.payload.operatorID)
              }
              break
            case TIM.TYPES.GRP_TIP_MBR_QUIT:
              if (item.payload.operatorID != this.uid) {
                // 自己加入群的话也会触发 所以这里过滤掉自己的userid 进群通知
                console.log('TIM user-leave', item.payload.operatorID)
                super.emit('user-leave', item.payload.operatorID)
              }
              break
          }
        }

        //
        if (item.to === this.channel && item.type === TIM.TYPES.MSG_CUSTOM) {
          try {
            console.log('TIM message', item)
            const data = JSON.parse(item.payload.data)
            const { GPBType, msgBase64 } = data
            const method = EncodeOrDecode.getMethod(GPBType)
            if (GPBType === 1666) return

            // 检查method是否有效
            if (!method) {
              console.warn(`TIM: 未知的GPBType: ${GPBType}`)
              return
            }

            let message: DeMessage | undefined = {
              method,
              GPBType,
              payload: data
            }
            if (msgBase64) {
              const decode = new EncodeOrDecode(method)
              message = decode.decode(data)
            }

            if (message) {
              console.log(`TIM %con ${method}-${GPBType} :`, 'color: #FFFFFF;background-color: #4CAF50;', message.payload)
              super.emit(message.method, item.from, message.payload)
            } else {
              console.log(`TIM %con ${method}-${GPBType} 解析为空，可能是分包的子包:`, 'color: #FFFFFF;background-color: #4CAF50;', data)
            }
          } catch (error) {
            console.error('TIM:ERROR Caught in the callback function of the event MESSAGE_RECEIVED:', error)
          }
        }
      })
    })
  }

  public async sendMessage(method: string, payload: object, peerId?: string) {
    if (!this.client) return
    const GPBType = EncodeOrDecode.getGPBType(method)
    const encode = new EncodeOrDecode(method)
    let messageArr: MessageType[] = []

    try {
      messageArr = encode.encode({ ...payload, msgInfo: { msgVersion: this.msgVersion } })
      console.log(`TIM %csend ${method}-${GPBType}`, 'color: #FFFFFF;background-color: black;', payload)
    } catch (error) {
      console.error(`TIM %csend ${method}-${GPBType}`, 'color: #FFFFFF;background-color: black;', payload)
      console.error(error)
    }
    messageArr.forEach((v) => {
      try {
        let id: string = ''
        let type: any = ''
        if (peerId) {
          id = peerId
          type = TIM.TYPES.CONV_C2C
        } else {
          id = this.channel
          type = TIM.TYPES.CONV_GROUP
        }
        const text = JSON.stringify({ ...v, timeInterval: +new Date() / 1000, channel: this.channel })
        const message = this.client.createCustomMessage({
          to: id,
          conversationType: type,
          payload: {
            data: text
          }
        })
        this.client.sendMessage(message)
      } catch (error) {
        console.error(error)
      }
    })
  }

  public async leave() {
    console.log('TIM leave')
    if (this.client) {
      await this.client
        .quitGroup(this.channel)
        .then((response) => {
          this.channel = ''
        })
        .catch(function (error) {
          console.warn('TIM quitGroup error:', error) // 退出群组失败的相关信息
          return false
        })
    }
    return true
  }

  public async logout() {
    this.removeAllListeners()
    console.log('TIM logout')
    if (this.channel != '') {
      this.leave()
    }

    await this.client.logout().catch((error: any) => {
      console.error('TIM 退出登录失败', error)
      return false
    })

    return true
  }

  // 重写removeAllListeners方法，清理所有监听器
  public removeAllListeners(event?: string | symbol): this {
    // 清理EventEmitter的监听器
    super.removeAllListeners(event)

    // 清理TIM SDK的监听器
    if (this.client) {
      try {
        // TIM SDK没有通用的removeAllListeners方法
        // 我们需要重新创建client实例来彻底清理所有监听器
        console.log('TIM: 重新创建client实例以清理所有监听器')
        const oldAppId = this.appId
        this.client = TIM.create({ SDKAppID: oldAppId })
      } catch (error) {
        console.warn('TIM removeAllListeners error:', error)
      }
    }

    return this
  }
}
